import { FC, useContext, useEffect, useRef } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePageContext } from "../../context/pageContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import useProcessingRequestApi from "../../http/processingRequest/api";
import {
  formatStatus,
  ItemGroupedProcessingRequests,
  ProcessingRequestStatus,
} from "../../http/processingRequest/types";
import { ProcessingTypeViews } from "../../http/processingType/type";
import { PaginationQuery } from "../../types";
import { downloadFileFromData, formatMinutes, formatPrice } from "../../utils";
import Button from "../ui/Button";
import Table from "../ui/Table";
import TH from "../ui/TH";
import ProcessingRequests from "./ProcessingRequests";

const ItemGroupedRequests: FC = () => {
  const filters = useListFilters({ isSearchable: true, prefix: "i_" });
  const debouncedValue = useDebounce(filters.state.q);
  const processingRequestApi = useProcessingRequestApi();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());

  const pageContext = usePageContext();

  useEffect(() => {
    pageContext.setPage({ url: "requests", title: i18n.t.common.requests }, undefined, i18n.t.common.groupedByItems);
  }, [i18n.language]);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = processingRequestApi.useItemGroupedRequest(query);

  useEffect(() => {
    error && dispatch(showErrorNotification(i18n, error));
  }, [error]);

  const filterOptions = {
    get: (fieldName: string) => processingRequestApi.getFieldFiltersQuery(fieldName, Object.values(ProcessingTypeViews)),
    update: filters.setFilters,
    getSelectedOptions: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const views = {
    title: i18n.t.common.groupedByItems,
    items: [
      {
        label: i18n.t.common.allRequests,
        url: `/requests`,
      },
      {
        label: i18n.t.common.onGoing,
        url: `/requests/on_going`,
      },
      {
        label: i18n.t.common.closedAndDiscarded,
        url: `/requests/closed_and_discarded`,
      },
      {
        label: i18n.t.common.helmets,
        url: `/requests/helmets`,
      },
    ],
  };

  return (
    <Table<ItemGroupedProcessingRequests>
      views={views}
      items={data?.items}
      total={data?.total}
      error={error}
      head={
        <tr>
          <th></th>
          <TH
            fieldName="processingRequest.status"
            title={i18n.t.common.status}
            filters={filterOptions}
            formatter={(value) => formatStatus(value as ProcessingRequestStatus, i18n)}
          />
          <th align="left">{i18n.t.common.finalItemCode}</th>
          <TH
            showTitleHover={i18n.t.groupedRequests.numberOfRequests}
            title={i18n.t.groupedRequests.short.numberOfRequests}
            fieldName="nrOfRequests"
            order={orderOptions}
          />
          <TH
            showTitleHover={i18n.t.groupedRequests.totalExpectedMinutes}
            title={i18n.t.groupedRequests.short.totalExpectedMinutes}
            fieldName="expectedTotalMinutes"
            order={orderOptions}
          />
          <TH
            showTitleHover={i18n.t.groupedRequests.totalExpectedMinutesCost}
            title={i18n.t.groupedRequests.short.totalExpectedMinutesCost}
            fieldName="totalExpectedMinutesCosts"
            order={orderOptions}
          />
          <TH
            showTitleHover={i18n.t.groupedRequests.productionTotalMinutes}
            title={i18n.t.groupedRequests.short.productionTotalMinutes}
            fieldName="productionTotalMinutes"
            order={orderOptions}
          />
          <TH
            showTitleHover={i18n.t.groupedRequests.totalProductionMinutesCost}
            title={i18n.t.groupedRequests.short.totalProductionMinutesCost}
            fieldName="totalProductionMinutesCosts"
            order={orderOptions}
          />
          <TH
            showTitleHover={i18n.t.groupedRequests.totalInitialItemsCost}
            title={i18n.t.groupedRequests.short.totalInitialItemsCost}
            fieldName="totalItemsCosts"
            order={orderOptions}
          />
          <TH
            showTitleHover={i18n.t.groupedRequests.totalPackagingsAndLabelsCost}
            title={i18n.t.groupedRequests.short.totalPackagingsAndLabelsCost}
            fieldName="totalPackagingsLabelsCosts"
            order={orderOptions}
          />
        </tr>
      }
      renderRow={(item) => (
        <>
          <td></td>
          <td>{item.item.code}</td>
          <td>{item.nrOfRequests}</td>
          <td>{formatMinutes(item.expectedTotalMinutes, i18n.language)}</td>
          <td>{formatPrice(item.totalExpectedMinutesCosts, i18n.language)}</td>
          <td>{formatMinutes(item.productionTotalMinutes, i18n.language)}</td>
          <td>{formatPrice(item.totalProductionMinutesCosts, i18n.language)}</td>
          <td>{formatPrice(item.totalItemsCosts, i18n.language)}</td>
          <td>{formatPrice(item.totalPackagingsLabelsCosts, i18n.language, { min: 2, max: 4 })}</td>
        </>
      )}
      secondaryButton={
        <Button
          buttonProps={{
            onClick: async () => {
              const r = await processingRequestApi.exportExcelGroupedRequests(query, aborter.current.signal);
              r && downloadFileFromData(r.data, "grouped_requests", r.contentType);
            },
          }}
        >
          {i18n.t.common.exportExcel}
        </Button>
      }
      searchOptions={{
        onChange: () => {
          aborter.current.abort();
          aborter.current = new AbortController();
        },
      }}
      filters={filters}
      innerTable={(item, i) => {
        const filteredStatuses = filters.getFilters("processingRequest.status");
        return (
          <ProcessingRequests
            views={Object.values(ProcessingTypeViews)}
            isInnerTable
            searchTerm={query.q}
            customFilters={[
              {
                fieldname: "processingRequest.status",
                values: filteredStatuses,
              },
              {
                fieldname: "processingRequest.finalItems.code",
                values: [item.item.code],
              },
            ]}
            prefix={`r_${i}_`}
          />
        );
      }}
    />
  );
};

export default ItemGroupedRequests;
